import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ParamMap, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../../core/services';
import { Provider } from '../../features/provider/models';
import { PendingProviderSelectionService } from './pending-provider-selection.service';

@Injectable({
    providedIn: 'root'
})
export class ProviderLinkService {
    providerId: number | undefined;
    programIds: number[] | undefined;
    provider$: Observable<Provider | undefined> | undefined;

    constructor(
        private readonly _api: ApiService,
        private readonly _router: Router,
        private readonly _pendingSelection: PendingProviderSelectionService
    ) {}

    get hasProvider(): boolean {
        return (
            this.providerId !== null &&
            this.providerId !== undefined &&
            this.providerId > 0
        );
    }

    /**
     * Drops the in-memory deep-link state.
     *
     * This service is `providedIn: 'root'` and nothing used to clear these fields, so a tab
     * that had ever loaded `/{client}/providers/{id}` kept that id for the rest of its life.
     * Loading a second client's portal over the first therefore left `hasProvider` true with
     * an id belonging to the *previous* client, and `signOut()` would navigate to
     * `/{newClient}/providers/{oldClientsProviderId}` (LA-22939).
     *
     * Deliberately NOT called on sign-out: landing a signed-out student back on their
     * provider's page is intended behaviour, and it needs `providerId` to still be here.
     */
    reset(): void {
        this.providerId = undefined;
        this.programIds = undefined;
        this.provider$ = undefined;
    }

    /**
     * Forgets a deep link the API would not resolve.
     *
     * The lookup is by clientId + providerId and filters on visibility, so an empty result is the
     * API saying this is not one of the client's providers — a mistyped id, a retired provider, or
     * another client's id (`/ga/providers/400` names a Tennessee provider). Nothing about it may
     * reach an application, so the in-memory link goes along with the staged selection: leaving
     * `providerId` behind would let `getProviderIdWithFallback()` hand the same id back out.
     */
    discardInvalidLink(): void {
        this.reset();
        this._pendingSelection.discardStagedLink();
    }

    /**
     * Re-writes the staged selection from the deep link this tab came in through.
     *
     * The source is the in-memory `providerId`, not the router: `checkForParams` puts it here and
     * it stays until a route without a `providerId` replaces it or `reset()` drops it.  So it can
     * outlive the page it was read from — a student who followed a deep link and then walked into
     * the application flow still has it set.  That is safe rather than accidental, because such a
     * student's selection *is* that provider: the agency lock keeps them off the provider search,
     * and the `checkForParams` pass that would release the lock is the same one that clears
     * `providerId`.  The two move together, so this cannot re-stage a link the student has left
     * behind.
     *
     * `AuthService.endSession()` clears the staged keys, and its two navigating callers then go to
     * `/{client}/providers/{id}` on the assumption that the navigation re-runs `checkForParams`.
     * It does not when the student is already on that URL: the primary outlet is unchanged, so the
     * navigation is discarded (`onSameUrlNavigation` defaults to `'ignore'`) and even on `'reload'`
     * HomeComponent is reused and its `ngOnInit` never runs again.  A student who opened the
     * sign-up dialog from their provider's own page therefore finished sign-up with nothing staged
     * and was dropped on the statewide provider search — the LA-22939 symptom, by a different
     * route.
     *
     * Does nothing once `reset()` has dropped the link, so the two cases that must not come back
     * (a client-code change, and a link the API refused) stay cleared.
     */
    restageCurrentLink(): void {
        if (!this.hasProvider) return;

        this.captureSource();
        this.updateSessionStorage();
    }

    getProviderIdWithFallback(): number | undefined {
        if (this.hasProvider && this.providerId) {
            return this.providerId;
        }
        return this._pendingSelection.providerIds[0];
    }

    get hasPrograms(): boolean {
        return (
            this.programIds !== null &&
            this.programIds !== undefined &&
            this.programIds.length > 0 &&
            this.programIds.every((id) => id > 0)
        );
    }

    checkForParams(params: ParamMap) {
        const providerId = params.get('providerId');
        this.providerId =
            providerId !== null && providerId !== undefined && +providerId > 0
                ? +providerId
                : undefined;

        this.programIds = this.parseProgramIds(params);

        this.captureSource();

        if (this.hasProvider) {
            this.updateSessionStorage();
            this.getProvider();
            return;
        }

        // No providerId in the route: the statewide home page.
        //
        // Program ids are cleared because they only ever qualify a deep-linked provider — they
        // come from the `/programs/:programIds` segment, so a route without a provider cannot
        // have them.
        //
        // The staged *provider* ids are deliberately left alone. Clearing them here is what
        // silently discarded a deep-linked selection whenever something bounced the student to
        // /home mid-flow: activating a route is not the student's decision to abandon their
        // provider. Dropping a selection stays where it belongs — the provider search page
        // writes the emptied list itself (ProviderCardCollectionComponent), and
        // AuthService.endSession() still clears everything on sign-out and expiry (LA-22939).
        this._pendingSelection.setProgramIds([]);
        this.provider$ = undefined;
    }

    /**
     * Record which portal pathway the student entered through (LA-22626).
     * Set whenever a provider link is processed. If the student navigates between statewide and agency portal,
     * it updates to reflect the current pathway (which deep link they're currently viewing).
     * Entering through an agency-specific provider link => 'Agency Portal', otherwise the statewide link => 'Statewide Portal'.
     * Sent to the API on the first application update and persisted on the application.
     */
    private captureSource(): void {
        const source = this.hasProvider ? 'Agency Portal' : 'Statewide Portal';
        this._pendingSelection.setSource(source);
    }

    cleanProgramIds(provider: Provider | undefined): void {
        if (
            this.programIds === null ||
            this.programIds === undefined ||
            this.programIds.length <= 0
        ) {
            this.programIds = undefined;
            this.updateSessionStorage();
            this._router.navigate([
                `${runtimeConfig.clientCode}/providers/${this.providerId}`
            ]);
            return;
        }

        const realIds = provider?.programs.map((p) => p.programId);
        const routeIds = this.programIds;

        if (
            realIds === null ||
            realIds === undefined ||
            realIds.length <= 0 ||
            routeIds === null ||
            routeIds === undefined ||
            routeIds.length <= 0
        ) {
            this.programIds = undefined;
            this.updateSessionStorage();
            this._router.navigate([
                `${runtimeConfig.clientCode}/providers/${this.providerId}`
            ]);
            return;
        }

        if (realIds!.sort() != routeIds!.sort()) {
            const onlyRealIds = routeIds!.filter((id) => realIds?.includes(id));
            this.programIds = onlyRealIds;
            this.updateSessionStorage();
            this._router.navigate([
                `${runtimeConfig.clientCode}/providers/${this.providerId}/programs/${onlyRealIds}`
            ]);
        }
    }

    private updateSessionStorage(): void {
        this._pendingSelection.setProgramIds(
            this.hasPrograms ? <number[]>this.programIds : []
        );
        this._pendingSelection.setProviderIds(
            this.hasProvider ? [<number>this.providerId] : []
        );
    }

    private getProvider(): void {
        let httpParams = new HttpParams();
        httpParams = httpParams.append('clientId', runtimeConfig.clientId);
        httpParams = httpParams.append('providerIds', <number>this.providerId);
        if (this.hasPrograms) {
            this.programIds?.forEach(
                (id) => (httpParams = httpParams.append('programIds', id))
            );
        }
        this.provider$ = this._api
            .get('/providers', httpParams)
            .pipe(
                map((result) =>
                    result?.providers?.length > 0
                        ? result.providers[0]
                        : undefined
                )
            );
    }

    private parseProgramIds(params: ParamMap): number[] | undefined {
        const programIds = params.getAll('programIds');
        if (programIds.length > 1) {
            return programIds.map((id) => +id).filter(Number);
        } else if (programIds.length === 1) {
            return programIds[0]
                .split(',')
                .map((id) => +id)
                .filter(Number);
        } else {
            return undefined;
        }
    }
}
